import { ArrowRight, CalendarClock, Clipboard, DoorOpen, LoaderCircle, RefreshCw, ShieldCheck } from "lucide-react";
import { useCallback, useEffect, useRef, useState, type ClipboardEvent } from "react";
import { useLocation, useNavigate } from "react-router";
import { AppShell } from "../components/AppShell";
import { StatusNotice } from "../components/StatusNotice";
import { useAuth } from "../features/auth/AuthContext";
import { savePrejoinTicket } from "../features/room/roomSession";
import { ApiError, api, assertRoomResolution, getApiErrorMessage } from "../lib/api";
import { isCompleteRoomCode, normalizeRoomCode, roomCodeFromJoinInput } from "../lib/navigation";
import { formatZonedDateTime, formatZonedTime, viewerTimeZone } from "../lib/scheduling";
import { ui } from "../lib/ui";

interface ScheduledRoomNotice {
  scheduledAt: string;
  scheduledTimeZone: string;
  earlyEntryAt?: string;
}

function scheduledRoomNotice(error: unknown): ScheduledRoomNotice | null {
  if (!(error instanceof ApiError) || error.code !== "room_not_started") return null;
  const scheduledAt = error.fields?.scheduledAt;
  const scheduledTimeZone = error.fields?.scheduledTimeZone;
  const earlyEntryAt = error.fields?.earlyEntryAt;
  if (!scheduledAt || Number.isNaN(Date.parse(scheduledAt)) || !scheduledTimeZone) return null;
  return { scheduledAt, scheduledTimeZone, ...(earlyEntryAt && !Number.isNaN(Date.parse(earlyEntryAt)) ? { earlyEntryAt } : {}) };
}

function linkedCode(pathname: string, hash: string): string {
  return roomCodeFromJoinInput(`${pathname}${hash}`, window.location.origin);
}

export function JoinPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { loading: authLoading } = useAuth();
  const [code, setCode] = useState(() => linkedCode(location.pathname, location.hash));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [scheduled, setScheduled] = useState<ScheduledRoomNotice | null>(null);
  const [clipboardMessage, setClipboardMessage] = useState("");
  const activeResolution = useRef<{ code: string; controller: AbortController } | null>(null);
  const openedLinkedInvitation = useRef("");

  const resolve = useCallback(async (candidate: string) => {
    if (authLoading) return;
    const normalized = roomCodeFromJoinInput(candidate, window.location.origin);
    if (!isCompleteRoomCode(normalized) || activeResolution.current?.code === normalized) return;
    activeResolution.current?.controller.abort();
    const controller = new AbortController();
    activeResolution.current = { code: normalized, controller };
    setBusy(true);
    setError("");
    setScheduled(null);
    try {
      const result = assertRoomResolution(await api.rooms.resolve(normalized, controller.signal));
      if (controller.signal.aborted || activeResolution.current?.controller !== controller) return;
      savePrejoinTicket(result.room.id, result.prejoinTicket);
      navigate(`/r/${encodeURIComponent(result.room.id)}/prejoin`, { state: { room: result.room } });
    } catch (nextError) {
      if (controller.signal.aborted || activeResolution.current?.controller !== controller) return;
      const nextScheduled = scheduledRoomNotice(nextError);
      setScheduled(nextScheduled);
      setError(nextScheduled ? "" : getApiErrorMessage(nextError));
      if (nextScheduled) setClipboardMessage("");
      setBusy(false);
    } finally {
      if (activeResolution.current?.controller === controller) activeResolution.current = null;
    }
  }, [authLoading, navigate]);

  useEffect(() => () => activeResolution.current?.controller.abort(), []);

  useEffect(() => {
    const invitationCode = linkedCode(location.pathname, location.hash);
    if (location.search || location.hash) window.history.replaceState(window.history.state, "", "/join");
    const timer = window.setTimeout(() => {
      if (location.search && !isCompleteRoomCode(invitationCode)) {
        setError(ui("That invitation format could expose the room code. Ask the host for a fresh Talk link.", "Такая ссылка может показать код комнаты. Попросите ведущего прислать новую."));
      }
      if (isCompleteRoomCode(invitationCode)) {
        setCode(invitationCode);
        if (authLoading) {
          setClipboardMessage(ui("Invitation link opened. Checking your session…", "Ссылка открыта. Проверяем сессию…"));
          return;
        }
      }
      if (openedLinkedInvitation.current !== invitationCode && isCompleteRoomCode(invitationCode)) {
        openedLinkedInvitation.current = invitationCode;
        setClipboardMessage(ui("Invitation link opened. Taking you to device check…", "Ссылка открыта. Переходим к проверке устройств…"));
        void resolve(invitationCode);
      }
    }, 0);
    return () => window.clearTimeout(timer);
  }, [authLoading, location.hash, location.pathname, location.search, resolve]);

  function acceptJoinInput(text: string): boolean {
    const next = roomCodeFromJoinInput(text, window.location.origin);
    if (!next) return false;
    setCode(next);
    setError("");
    setScheduled(null);
    setClipboardMessage(text.includes("/join") || text.includes("#code=") ? ui("Invitation link pasted.", "Ссылка вставлена.") : ui("Code pasted.", "Код вставлен."));
    return true;
  }

  function onPaste(event: ClipboardEvent<HTMLInputElement>) {
    const text = event.clipboardData.getData("text");
    if (acceptJoinInput(text)) {
      event.preventDefault();
      return;
    }
    if (text.includes("://") || text.includes("/join")) {
      event.preventDefault();
      setClipboardMessage(ui("That link is not a valid Talk invitation. Ask the host for a fresh room link or code.", "Это не ссылка на комнату. Попросите ведущего прислать новую ссылку или код."));
    }
  }

  async function paste() {
    try {
      const text = await navigator.clipboard.readText();
      if (!acceptJoinInput(text)) throw new Error(ui("No room invitation found", "Приглашение не найдено"));
    } catch {
      setClipboardMessage(ui("No Talk invitation was found. Paste a room link or code into the field instead.", "Приглашение не найдено. Вставьте ссылку или код комнаты в поле."));
    }
  }

  const localTimeZone = scheduled ? viewerTimeZone() : "";
  const localTimeZoneDiffers = Boolean(scheduled && localTimeZone !== scheduled.scheduledTimeZone);

  return (
    <AppShell>
      <main id="main-content" className="join-page" tabIndex={-1}>
        <section className="join-copy"><DoorOpen aria-hidden="true" size={38} /><h1>{ui("Enter the room.", "Войти в комнату.")}</h1><p>{ui("Open an invitation link, paste one here, or enter its 12-character code. You can join as a guest; no account or installation is required.", "Откройте ссылку, вставьте её сюда или введите код из 12 символов. Гость может войти без аккаунта и без установки.")}</p><div className="join-trust"><ShieldCheck aria-hidden="true" size={18} /><span>{ui("The host controls admission, room lock, and guest access.", "Ведущий решает, кого пускать, когда закрыть комнату и можно ли гостям.")}</span></div></section>
        <section className="join-surface" aria-labelledby="join-form-title">
          <h2 id="join-form-title">{ui("Room link or code", "Ссылка или код комнаты")}</h2>
          <p>{ui("Invitation links open automatically. Separators and letter case do not matter.", "Ссылка открывается сама. Регистр и дефисы не важны.")}</p>
          <form onSubmit={(event) => { event.preventDefault(); void resolve(code); }}>
            <label className="field field--code"><span className="sr-only">{ui("Room link or code", "Ссылка или код комнаты")}</span><input autoFocus inputMode="text" autoCapitalize="characters" autoComplete="off" placeholder="ABCD-EFGH-JKMN" maxLength={14} value={code} onPaste={onPaste} onChange={(event) => { setCode(normalizeRoomCode(event.target.value)); setError(""); setClipboardMessage(""); }} disabled={busy} /></label>
            <button className="button button--primary button--wide" type="submit" disabled={authLoading || busy || !isCompleteRoomCode(code)}>{authLoading || busy ? <LoaderCircle className="spin" aria-hidden="true" size={19} /> : <ArrowRight aria-hidden="true" size={19} />}{authLoading ? ui("Checking session…", "Проверяем сессию…") : busy ? ui("Opening room…", "Открываем комнату…") : ui("Continue", "Продолжить")}</button>
          </form>
          <button className="paste-button" type="button" onClick={() => void paste()} disabled={busy}><Clipboard aria-hidden="true" size={17} />{ui("Paste room link or code", "Вставить ссылку или код")}</button>
          {clipboardMessage ? <p className="field-message" role="status">{clipboardMessage}</p> : null}
          {scheduled ? <section className="scheduled-room-notice" role="status" aria-live="polite"><CalendarClock aria-hidden="true" /><div><h3>{ui("This room is scheduled", "Комната запланирована")}</h3><time dateTime={scheduled.scheduledAt}>{formatZonedDateTime(scheduled.scheduledAt, scheduled.scheduledTimeZone)} · {scheduled.scheduledTimeZone}</time>{scheduled.earlyEntryAt ? <p>{ui(`Entry opens at ${formatZonedTime(scheduled.earlyEntryAt, scheduled.scheduledTimeZone)}.`, `Вход откроется в ${formatZonedTime(scheduled.earlyEntryAt, scheduled.scheduledTimeZone)}.`)}</p> : null}{localTimeZoneDiffers ? <p>{ui(`Your time: ${formatZonedDateTime(scheduled.scheduledAt, localTimeZone)} · ${localTimeZone}`, `У вас: ${formatZonedDateTime(scheduled.scheduledAt, localTimeZone)} · ${localTimeZone}`)}</p> : null}<button className="button button--secondary button--small" type="button" onClick={() => void resolve(code)} disabled={busy}><RefreshCw className={busy ? "spin" : ""} aria-hidden="true" />{ui("Check again", "Проверить снова")}</button></div></section> : null}
          {error ? <StatusNotice tone="danger" title={ui("Could not open that room", "Не удалось открыть комнату")}>{error}</StatusNotice> : null}
        </section>
      </main>
    </AppShell>
  );
}
